import { ApolloError } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfirm } from '@shared/components/Modal';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import {
  SubtableDraftsState,
  useSubtableDraftsState,
} from '@shared/scenes/entity/components/fields/SubtableEditField/subtableDrafts';
import { useSubtableRowLimitAlert } from '@shared/scenes/entity/components/fields/SubtableEditField/subtableRowLimit';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, RawFieldData, UpdateField } from '@shared/scenes/entity/types';
import { parseApolloError } from '@shared/scenes/entity/util/parseEntityErrors';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { useEntityFormViewTemplate } from '@shared/scenes/entity/util/useEntityFormViewTemplate';
import {
  applyComponentOverrides,
  useViewComponents,
} from '@shared/scenes/entity/util/useViewComponents';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isSubtableComponent, requiresViewTemplate } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useCreateEntityFields } from './useCreateEntityFields';
import { useSaveNewEntity } from './useSaveNewCardEntity';

type Status = 'loading' | 'error' | 'input' | 'success';

interface CreateCardEntity {
  components: API.AnyComponent[];
  fields: FieldRecord | null;
  errors: CardEntityError[];
  viewTemplateTitle: string;
  viewTemplate?: API.ViewTemplateFragment;
  updateField: UpdateField;
  onPressSave: () => void;
  isLoading: boolean;
  status: Status;
  setStatus: (status: Status) => void;
  isDirty: boolean;
  resetForm: () => void;
  savedEntity?: API.WorkflowEntity;
  /** The draft rows of the form's subtable fields, to provide through SubtableDraftsContext. */
  subtableDrafts: SubtableDraftsState;
  /**
   * The card was created but some of its draft rows weren't: the card's own fields are locked,
   * and saving again only retries the rows.
   */
  isRetryingRows: boolean;
  /** True once save has been attempted while the form has errors. Used by
   *  SubformNavigationContext to trigger per-subform error display. Resets when the form
   *  becomes fully valid (including subforms). */
  saveAttempted: boolean;
}

interface UseCreateCardEntityProps {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  queryFieldOverrides?: RawFieldData;
  /**
   * When true, hold off seeding the form until the source card for a duplicate
   * has loaded. See {@link useCreateEntityFields}.
   */
  isDuplicateLoading?: boolean;
  /** The card a new subtable row belongs to. */
  parentEntityId?: string | null;
  /** Called with the error when the create mutation rejects. */
  onSaveError?: (error: unknown) => void;
  /**
   * Saves into a draft instead of creating the entity: a subtable row on a card that doesn't
   * exist yet. Gets the form's fields and its resolved view template, once they're valid.
   */
  onSaveDraft?: (fields: FieldRecord, viewTemplateId?: string) => void;
  /** Seeds the form with these fields instead of the defaults; see useCreateEntityFields. */
  initialFields?: FieldRecord;
}

export function useCreateCardEntity({
  workflowTemplateId,
  viewTemplateId,
  queryFieldOverrides,
  isDuplicateLoading = false,
  parentEntityId,
  onSaveError,
  onSaveDraft,
  initialFields,
}: UseCreateCardEntityProps): CreateCardEntity {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const isViewTemplateRequired = requiresViewTemplate(workflowTemplate, viewTemplateId);
  const viewTemplate = useEntityFormViewTemplate({
    workflowTemplateId,
    viewTemplateId,
    type: 'CREATE',
  });
  const [status, setStatus] = useState<Status>('loading');
  const [saveAttemptedWithLocalErrors, setSaveAttemptedWithLocalErrors] = useState(false);
  const [batchCreateError, setBatchCreateError] = useState<CardEntityError[] | null>(null);
  const [savedEntity, setSavedEntity] = useState<API.WorkflowEntity>();
  const { shouldBatchCreate, batchCreate } = useBatch();
  const subtableDrafts = useSubtableDraftsState();
  // The card, once it exists while some of its draft rows still don't.
  const [createdEntity, setCreatedEntity] = useState<API.WorkflowEntity>();
  const [rowSaveError, setRowSaveError] = useState<CardEntityError[] | null>(null);
  const [isSavingRows, setIsSavingRows] = useState(false);
  const confirm = useConfirm();
  const showRowLimitAlert = useSubtableRowLimitAlert();
  const {
    fields,
    originalFields,
    updateField,
    isDirty: isFieldsDirty,
    resetFields,
  } = useCreateEntityFields({
    workflowTemplate,
    viewTemplate,
    queryFieldOverrides,
    isDuplicateLoading,
    isViewTemplateRequired,
    initialFields,
  });
  // Hold fields as null until defaults have been applied, so downstream components
  // (e.g. CardEntityCreateFormContent) wait to mount field editors until data is ready.
  // Without this, field components mount with empty state and miss late-arriving values
  // like fromEntity overrides used by card duplication.
  const initializedFields = isNil(originalFields) ? null : fields;

  const {
    saveEntity,
    loading: saveEntityLoading,
    errors: apiErrors,
    // Pass the resolved view template's id (not the raw route slug prop, which is
    // nil when the create form resolves its template by behavior/exclusion). Without
    // it the server skips all component required-ness checks. Mirrors the edit path
    // in useEditCardEntity, which passes viewTemplate?.id.
  } = useSaveNewEntity(workflowTemplateId, viewTemplate?.id, parentEntityId);

  const viewComponents = useViewComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents: false,
    includeInputComponentsOnly: true,
  });
  // Once the card exists, only its subtables can still change, to fix up rows that failed.
  const isRetryingRows = !isNil(createdEntity);
  const components = useMemo(() => {
    if (!isRetryingRows) {
      return viewComponents;
    }
    return viewComponents.map(component =>
      isSubtableComponent(component) ? component : { ...component, readonly: true },
    );
  }, [viewComponents, isRetryingRows]);

  // The full component set, overrides applied and nothing filtered out — it keeps the managed DATE
  // children a DATE_RANGE owns, which `components` drops. Resolves required-ness for managing
  // components. Fields below the form read the same set from WorkflowComponentsContext.
  const workflowComponents = useMemo(
    () => applyComponentOverrides(workflowTemplate?.components ?? [], viewTemplate),
    [workflowTemplate?.components, viewTemplate],
  );

  const { errors, isValid } = useFormErrors({
    apiErrors: rowSaveError ?? batchCreateError ?? apiErrors,
    fields,
    components,
    workflowComponents,
    includeOnSubmitErrors: saveAttemptedWithLocalErrors,
    includeLocalErrors: saveAttemptedWithLocalErrors,
  });

  // Subform internal required errors are not tracked in useFormErrors (they belong to
  // SubformErrorContext for per-component display control). Block the mutation directly.
  const hasSubformRequiredErrors = useMemo(
    () =>
      components.some(component => {
        const field = fields?.[component.id];
        return field instanceof SubformField && field.hasUnfilledRequiredFields();
      }),
    [components, fields],
  );

  useEffect(() => {
    setStatus(prevStatus => {
      if (prevStatus === 'success') {
        return prevStatus;
      }
      const isFieldsReady = !isNil(initializedFields);
      const isViewTemplateReady = !isViewTemplateRequired || !isNil(viewTemplate);
      if (saveEntityLoading || isSavingRows || !isFieldsReady || !isViewTemplateReady) {
        return 'loading';
      } else if (!isEmpty(errors)) {
        return 'error';
      } else {
        return 'input';
      }
    });
  }, [
    errors,
    saveEntityLoading,
    isSavingRows,
    status,
    initializedFields,
    viewTemplate,
    isViewTemplateRequired,
  ]);

  useEffect(() => {
    if (isValid && !hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(false);
    }
  }, [isValid, hasSubformRequiredErrors]);

  // Creates the card's draft rows under it, then finishes the save. Rows that fail stay as drafts,
  // and the form stays open on the created card so they can be retried.
  const saveRows = useCallback(
    async (entity: API.WorkflowEntity) => {
      setRowSaveError(null);
      setIsSavingRows(true);
      const failedCount = await subtableDrafts.saveRows(entity.id);
      setIsSavingRows(false);
      if (failedCount > 0) {
        setCreatedEntity(entity);
        setRowSaveError([
          {
            description: i18n('card.blocks.subtableInput.rowsNotAdded', { count: failedCount }),
          },
        ]);
        setStatus('error');
        return;
      }
      setStatus('success');
      setSavedEntity(entity);
    },
    [subtableDrafts],
  );

  const save = useCallback(async () => {
    if (!isValid || hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(true);
      return;
    }
    setSaveAttemptedWithLocalErrors(false);

    if (!isNil(onSaveDraft)) {
      onSaveDraft(fields, viewTemplate?.id);
      setStatus('success');
      return;
    }
    if (subtableDrafts.isOverRowLimit) {
      void showRowLimitAlert();
      return;
    }
    if (subtableDrafts.hasDirtyRowForm) {
      const confirmed = await confirm({
        title: i18n('card.blocks.subtableInput.unaddedRowTitle'),
        message: i18n('card.blocks.subtableInput.unaddedRowMessage'),
        confirmLabel: i18n('card.create.submit'),
      });
      if (!confirmed) {
        return;
      }
    }
    if (!isNil(createdEntity)) {
      await saveRows(createdEntity);
      return;
    }

    if (shouldBatchCreate) {
      try {
        const result = await batchCreate({
          workflowTemplateId: workflowTemplateId ?? '',
          fields: toFieldJson(fields, workflowTemplate),
        });
        const entities = result?.data?.createRecurringEntities?.workflowEntitiesResult?.entities;
        const entity = entities?.[0];
        if (!entity) {
          throw new Error('No entity returned from batch create');
        }
        // Draft rows go to the first card of the batch only.
        await saveRows(entity);
      } catch (error) {
        setBatchCreateError(
          error instanceof ApolloError
            ? parseApolloError(error)
            : [{ description: i18n('card.form.errors.generic') }],
        );
        setStatus('error');
      }
    } else {
      try {
        const result = await saveEntity(fields);
        const entity = result.data?.createWorkflowEntity?.entity;
        if (!entity) {
          throw new Error('No entity returned from saveEntity');
        }
        await saveRows(entity);
      } catch (error) {
        setStatus('error');
        onSaveError?.(error);
      }
    }
  }, [
    fields,
    saveEntity,
    isValid,
    hasSubformRequiredErrors,
    shouldBatchCreate,
    batchCreate,
    workflowTemplateId,
    workflowTemplate,
    onSaveError,
    onSaveDraft,
    viewTemplate?.id,
    subtableDrafts,
    showRowLimitAlert,
    confirm,
    createdEntity,
    saveRows,
  ]);

  const resetForm = useCallback(() => {
    resetFields();
    subtableDrafts.reset();
    setCreatedEntity(undefined);
    setRowSaveError(null);
    setStatus('input');
  }, [resetFields, subtableDrafts, setStatus]);

  // Draft rows are unsaved changes too, as is a row form with edits not yet added.
  const isDirty = isFieldsDirty || subtableDrafts.hasRows || subtableDrafts.hasDirtyRowForm;

  return {
    components,
    fields: initializedFields,
    errors,
    viewTemplateTitle: viewTemplate?.name ?? '',
    viewTemplate,
    updateField,
    onPressSave: save,
    isLoading: status === 'loading',
    status,
    isDirty,
    setStatus,
    resetForm,
    savedEntity,
    subtableDrafts,
    isRetryingRows,
    saveAttempted: saveAttemptedWithLocalErrors,
  };
}
